import React, { useState, useEffect } from "react";
import { Sparkles, Check, Phone, Mail, User, Loader2 } from "lucide-react";
import { getCustomerToken } from "../utils/customerAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable lead-capture card for ALL viral / top-of-funnel surfaces.
 *
 * Props:
 *  - source:   required. One of party_dna | creator_studio | magic | split_pay | smart_invite | etc.
 *  - extra:    optional metadata object (personality, occasion, package, etc.) — merged into the lead doc.
 *  - headline: optional headline text override.
 *  - subheadline: optional subline override.
 *  - cta:      optional CTA button text override.
 *  - variant:  "light" (default, on light bg) | "dark" (on dark bg).
 *  - onSubmitted: optional callback fired with the saved lead on success.
 *
 * Behaviour:
 *  - Once submitted, locks to a "you're in" thank-you card and remembers in localStorage
 *    for that source (so we don't pester repeat-visitors of the same surface).
 */
export const LeadCaptureCard = ({
  source,
  extra,
  headline,
  subheadline,
  cta,
  variant = "light",
  onSubmitted,
}) => {
  const storageKey = `dg_lead_${source}`;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey)) setDone(true);
    } catch (_) { /* localStorage may be blocked */ }
  }, [storageKey]);

  // Auto-prefill from logged-in customer profile (single source of truth).
  // No more "I have to type my name + phone every time I see this card."
  useEffect(() => {
    const token = getCustomerToken();
    if (!token) return;
    fetch(`${API_URL}/api/customer-auth/me`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        if (d?.name) setName(prev => prev || d.name);
        if (d?.phone) setPhone(prev => prev || d.phone);
        if (d?.email) setEmail(prev => prev || d.email);
      })
      .catch(() => { /* not logged in — fine, user fills manually */ });
  }, []);

  const isPhoneValid = (val) => /^[+]?[\d\s-]{8,15}$/.test(val.trim());

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!name.trim()) { setError("Please enter your name"); return; }
    if (!isPhoneValid(phone)) { setError("Please enter a valid phone number"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          source,
          extra: extra || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Couldn't save (HTTP ${res.status})`);
      }
      const saved = await res.json();
      try { localStorage.setItem(storageKey, "1"); } catch (_) { /* ignore */ }
      setDone(true);
      if (onSubmitted) onSubmitted(saved);
    } catch (err) {
      setError(err.message || "Something went wrong. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = variant === "dark";
  const baseBg = isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200";
  const titleColor = isDark ? "text-white" : "text-[#1E1B4B]";
  const subColor = isDark ? "text-white/60" : "text-[#475569]";
  const inputBg = isDark
    ? "bg-white/10 border-white/15 text-white placeholder-white/40 focus:border-[#E83E8C]"
    : "bg-[#F8FAFC] border-gray-200 text-[#1E1B4B] placeholder-[#94A3B8] focus:border-[#E83E8C]";

  if (done) {
    return (
      <div
        className={`rounded-2xl border p-5 ${baseBg} text-center`}
        data-testid={`lead-capture-success-${source}`}
      >
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/15 mb-2">
          <Check size={18} className="text-emerald-500" />
        </div>
        <p className={`text-sm font-bold ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
          You're on the list!
        </p>
        <p className={`text-xs mt-1 ${subColor}`}>
          Our team will WhatsApp you within 24 hours to plan your perfect setup.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-2xl border p-5 ${baseBg} space-y-3`}
      data-testid={`lead-capture-${source}`}
    >
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[#E83E8C]" />
        <p className={`text-sm font-bold ${titleColor}`}>
          {headline || "Loved it? Let's plan it for real."}
        </p>
      </div>
      <p className={`text-xs ${subColor}`}>
        {subheadline ||
          "Drop your number — our team will reach out within 24 hours with a tailored quote and free consultation."}
      </p>

      <div className="space-y-2">
        <div className="relative">
          <User size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${subColor}`} />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className={`w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm outline-none transition-colors ${inputBg}`}
            data-testid={`lead-capture-name-${source}`}
            autoComplete="name"
          />
        </div>
        <div className="relative">
          <Phone size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${subColor}`} />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number (e.g. 9876543210)"
            className={`w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm outline-none transition-colors ${inputBg}`}
            data-testid={`lead-capture-phone-${source}`}
            autoComplete="tel"
            inputMode="tel"
          />
        </div>
        <div className="relative">
          <Mail size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${subColor}`} />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className={`w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm outline-none transition-colors ${inputBg}`}
            data-testid={`lead-capture-email-${source}`}
            autoComplete="email"
          />
        </div>
      </div>

      {error && (
        <p
          className="text-xs text-red-500 font-medium"
          data-testid={`lead-capture-error-${source}`}
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white text-sm font-bold disabled:opacity-50 hover:opacity-95 transition-all flex items-center justify-center gap-2"
        data-testid={`lead-capture-submit-${source}`}
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Saving...
          </>
        ) : (
          cta || "Get My Free Quote"
        )}
      </button>

      <p className={`text-[10px] text-center ${subColor}`}>
        We respect your privacy. No spam — just a quick chat about your party.
      </p>
    </form>
  );
};

export default LeadCaptureCard;
